/**
 * 工具相关API (v2 多用户版本)
 */

import type { ApiResponse } from './authApi';

export interface Tool {
  id: string;
  server_id: string;
  server_name: string;
  name: string;
  display_name: string;
  description?: string;
  input_schema: any;
  is_published: boolean;
  category?: string;
  tags?: string[];
  published_at?: string;
  published_by?: string;
  subscription_count: number;
  created_at: string;
  updated_at: string;
  metadata?: any;
}

export interface PaginatedResult<T> {
  data: T[];
  pagination: {
    page: number;
    page_size: number;
    total: number;
    total_pages: number;
  };
}

class ToolsAPIV2 {
  private baseURL = '/api/v2';

  /**
   * 获取已发布的工具列表
   */
  async getPublishedTools(
    params?: { page?: number; page_size?: number; keyword?: string },
    token?: string
  ): Promise<ApiResponse<PaginatedResult<Tool>>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    if (params?.keyword) queryParams.append('keyword', params.keyword);

    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseURL}/tools/published?${queryParams}`, {
      headers,
    });

    if (!response.ok) {
      throw new Error('获取工具列表失败');
    }

    return response.json();
  }

  /**
   * 获取工具详情
   */
  async getToolDetail(toolId: string, token?: string): Promise<ApiResponse<Tool>> {
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseURL}/tools/${toolId}`, {
      headers,
    });

    if (!response.ok) {
      throw new Error('获取工具详情失败');
    }

    return response.json();
  }

  /**
   * 搜索工具
   */
  async searchTools(keyword: string, token?: string): Promise<ApiResponse<Tool[]>> {
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseURL}/tools/search?q=${encodeURIComponent(keyword)}`, {
      headers,
    });

    if (!response.ok) {
      throw new Error('搜索工具失败');
    }

    return response.json();
  }

  /**
   * 发布工具（管理员）
   */
  async publishTool(toolId: string, token: string): Promise<ApiResponse> {
    const response = await fetch(`${this.baseURL}/tools/${toolId}/publish`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || '发布工具失败');
    }

    return response.json();
  }

  /**
   * 通过名称发布工具（管理员）
   * 使用工具名称而不是ID
   */
  async publishToolByName(toolName: string, token: string): Promise<ApiResponse> {
    const response = await fetch(`${this.baseURL}/tools/publish-by-name`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name: toolName }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || '发布工具失败');
    }

    return response.json();
  }

  /**
   * 取消发布工具（管理员）
   */
  async unpublishTool(toolId: string, token: string): Promise<ApiResponse> {
    const response = await fetch(`${this.baseURL}/tools/${toolId}/unpublish`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || '取消发布失败');
    }

    return response.json();
  }

  /**
   * 通过名称取消发布工具（管理员）
   * 使用工具名称而不是ID
   */
  async unpublishToolByName(toolName: string, token: string): Promise<ApiResponse> {
    const response = await fetch(`${this.baseURL}/tools/unpublish-by-name`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name: toolName }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || '取消发布失败');
    }

    return response.json();
  }
}

export const toolsAPIV2 = new ToolsAPIV2();

